import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { use_dispatch } from '@/hooks/_base/use_dispatch';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_user, use_slice_workspace } from '@/hooks/store/use_slices';
import { useToast } from '@/hooks/use-toast';
import i18n_base_ui from '@/locales/keys/i18n_base_ui.json';
import { ENUM__i18n_namespace } from '@/services/i18n/ENUM__i18n_namespace';
import { remote_workspace_transfer_ownership_proposal_reply } from '@/state/workspace/ownership/remote_workspace_transfer_ownership_proposal_reply';
import { RequestWorkspaceTransferOwnershipReply } from '@shared/dto/+workspace/workspace_transfer_ownership_proposal/_requests/request_workspace_transfer_ownership_reply';
import { DTOWorkspaceTranserOwnershipProposal } from '@shared/dto/+workspace/workspace_transfer_ownership_proposal/dto_workspace_transfer_ownership_proposal';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';

export function WatcherWorkspaceTransferProposal() {
  const { t } = useTranslation(ENUM__i18n_namespace.BASE_UI);

  const dispatch = use_dispatch();
  const { toast, dismiss } = useToast();

  const { user_id } = use_slice_user(['user_id']);
  const { transfer_ownership_proposals_map } = use_slice_workspace(['transfer_ownership_proposals_map']);

  //key - proposal_id, value - toast_id
  const toast_ids_map = useRef(new Map<string, string>());

  /*
   * EFFECTS
   */
  use_effect(() => {
    for (let proposal of transfer_ownership_proposals_map.values()) {
      if (proposal.owner_user_id == user_id) continue; //dont show proposals from owner

      show_toast(proposal);
    }

    for (let [proposal_id, value] of toast_ids_map.current) {
      let invitation_exists = transfer_ownership_proposals_map.has(proposal_id);

      if (invitation_exists) continue;

      dismiss(value);

      toast_ids_map.current.delete(proposal_id);
    }
  }, [transfer_ownership_proposals_map]);

  /*
   * EVENTS
   */
  async function onButtonAcceptClicked(toast_id: string, proposal: DTOWorkspaceTranserOwnershipProposal) {
    dismiss(toast_id);
    toast_ids_map.current.delete(proposal._id);

    let request = new RequestWorkspaceTransferOwnershipReply();
    request.workspace_id = proposal.workspace_id;
    request.proposal_id = proposal._id;
    request.is_accepted = true;

    await dispatch(remote_workspace_transfer_ownership_proposal_reply(request));
  }

  async function onButtonRejectClicked(toast_id: string, proposal: DTOWorkspaceTranserOwnershipProposal) {
    dismiss(toast_id);
    toast_ids_map.current.delete(proposal._id);

    let request = new RequestWorkspaceTransferOwnershipReply();
    request.workspace_id = proposal.workspace_id;
    request.proposal_id = proposal._id;
    request.is_accepted = false;

    await dispatch(remote_workspace_transfer_ownership_proposal_reply(request));
  }

  /*
   * METHODS
   */

  function show_toast(proposal: DTOWorkspaceTranserOwnershipProposal) {
    let toast_data = toast({
      title: t(i18n_base_ui.toasts.workspace_transfer_proposal_received.title),
      description: t(i18n_base_ui.toasts.workspace_transfer_proposal_received.decription, {
        owner_full_name: proposal.owner_full_name,
        workspace_name: proposal.workspace_name,
      }),
      duration: Infinity,
      hide_button_close: true,
      action: (
        <>
          <UI_PRIMITIVE__button variant="outline-on-bar" onClick={() => onButtonAcceptClicked(toast_data.id, proposal)}>
            {t(i18n_base_ui.toasts.workspace_transfer_proposal_received.action.button.accept)}
          </UI_PRIMITIVE__button>
          <UI_PRIMITIVE__button variant="outline-on-bar" onClick={() => onButtonRejectClicked(toast_data.id, proposal)}>
            {t(i18n_base_ui.toasts.workspace_transfer_proposal_received.action.button.reject)}
          </UI_PRIMITIVE__button>
        </>
      ),
    });

    toast_ids_map.current.set(proposal._id, toast_data.id);
  }

  return null;
}
